import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { put, select, takeLatest } from 'redux-saga/effects';
import {
  fetchBulkAllocationDetailFailure,
  fetchBulkAllocationDetailReq,
  fetchBulkAllocationDetailSuccess,
  fetchBulkAllocationListFailure,
  fetchBulkAllocationListReq,
  fetchBulkAllocationListSuccess,
} from 'redux/slice/allocation/BulkAllocationList.slice';
import { RootState } from 'redux/store/store';
import {
  BulkAllocationDetailRequest,
  BulkAllocationDetailResponse,
  BulkAllocationListRequest,
  BulkAllocationListResponse,
} from 'redux/types/Allocation.types';
import { allocationEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

interface FetchBulkAllocationListPayload {
  page: number;
  pageSize: number;
  filters?: BulkAllocationListRequest;
}

function* fetchBulkAllocationListSaga(
  action: PayloadAction<FetchBulkAllocationListPayload>
) {
  try {
    const { page, pageSize, filters } = action.payload;

    const allocatorId: string = yield select(
      (state: RootState) => state.userConfiguration.entityId
    );

    // Build request body for v3 API with new filters format
    const requestBody: BulkAllocationListRequest = {};
    if (filters?.agencyId) {
      requestBody.agencyId = filters.agencyId;
    }
    if (filters?.search) {
      requestBody.search = filters.search;
    }
    // Convert status to new filters.columns format
    if (filters?.status) {
      requestBody.filters = {
        columns: [{ name: 'status', filterByValues: [filters.status] }],
      };
    }
    // If filters.filters is already provided in new format, use it directly
    if (filters?.filters) {
      requestBody.filters = filters.filters;
    }

    const response: { data: BulkAllocationListResponse } =
      yield HTTPClient.PostRequest(
        allocationEndpoints.GET_BULK_ALLOCATION_LIST(page, pageSize),
        requestBody,
        {
          headers: {
            'allocator-id': allocatorId,
            'Content-Type': 'application/json',
          },
        }
      );

    yield put(fetchBulkAllocationListSuccess(response.data));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch bulk allocation list';
    yield put(fetchBulkAllocationListFailure(errorMessage));
    message.error(errorMessage);
  }
}

function* fetchBulkAllocationDetailSaga(
  action: PayloadAction<BulkAllocationDetailRequest>
) {
  try {
    const {
      bulkUploadId,
      page = 1,
      pageSize = 10,
      ...filters
    } = action.payload;

    const allocatorId: string = yield select(
      (state: RootState) => state.userConfiguration.entityId
    );

    const response: { data: BulkAllocationDetailResponse } =
      yield HTTPClient.PostRequest(
        allocationEndpoints.GET_BULK_ALLOCATION_DETAIL(
          bulkUploadId,
          page,
          pageSize
        ),
        filters,
        {
          headers: {
            'allocator-id': allocatorId,
          },
        }
      );

    yield put(fetchBulkAllocationDetailSuccess(response.data));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch bulk allocation details';
    yield put(fetchBulkAllocationDetailFailure(errorMessage));
    message.error(errorMessage);
  }
}

export function* watchBulkAllocationListSagas() {
  yield takeLatest(
    fetchBulkAllocationListReq.type,
    fetchBulkAllocationListSaga
  );
  yield takeLatest(
    fetchBulkAllocationDetailReq.type,
    fetchBulkAllocationDetailSaga
  );
}
